'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useSession } from 'next-auth/react'
import Link from 'next/link'
import { AlertTriangle, CircleCheck, Gift, Info, LoaderCircle, Ticket, TicketPercent, Truck, X } from 'lucide-react'
import { useCart } from '@/lib/commerce/hooks'
import { formatDateTimeNormal, formatImageURL, formatPrice, isCouponActive, isGiftCardActive, isLoyaltyActive, beatenByMessage } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import { toast } from '@/lib/toast'
import { getLoyaltyVouchersApi, getLoyaltyTiersApi } from '@/lib/commerce/services/loyalty-api'

const inputStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,0.3)',
  border: '1px solid rgba(255,255,255,0.13)',
  color: 'white',
}

export default function DiscountTabs({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const { data: session } = useSession()
  const [couponCode, setCouponCode] = useState('')
  const [giftCardCode, setGiftCardCode] = useState('')
  const [loadingCoupon, setLoadingCoupon] = useState(false)
  const [loadingGiftCard, setLoadingGiftCard] = useState(false)
  const [couponCondition, setCouponCondition] = useState<any>(null)
  const [activeTab, setActiveTab] = useState<'coupon' | 'giftcard' | 'loyalty'>('coupon')
  const [loyaltyVouchers, setLoyaltyVouchers] = useState<any[]>([])
  const [loyaltyProgram, setLoyaltyProgram] = useState<any>(null)

  const { appliedCoupon, applyCoupon, removeCoupon, couponDiscount, appliedGiftCard, applyGiftCard, removeGiftCard, amount, sale, allProducts, appliedLoyaltyVoucher, applyLoyaltyVoucher, removeLoyaltyVoucher } = useCart()
  const subtotal = amount - sale

  const canUseCoupon = isCouponActive(vendor)
  const canUseGiftCard = isGiftCardActive(vendor)
  const loyaltyEnabled = isLoyaltyActive(vendor) && !!session?.user?.id
  const canUseLoyaltyVoucher = loyaltyEnabled && (loyaltyVouchers.length > 0 || !!appliedLoyaltyVoucher)
  const appliedVoucherAmount = Number(appliedLoyaltyVoucher?.remainingAmount ?? appliedLoyaltyVoucher?.amount) || 0
  const voucherExceedsCart = appliedVoucherAmount > subtotal

  useEffect(() => {
    if (!loyaltyEnabled) return
    let cancelled = false
    getLoyaltyVouchersApi({ vendorId: vendor?.id || vendor, session: session || undefined }).then(({ data }) => {
      if (!cancelled && Array.isArray(data)) setLoyaltyVouchers(data.filter((voucher: any) => voucher.status === 'active'))
    }).catch(() => {})
    getLoyaltyTiersApi({ vendorId: vendor?.id || vendor }).then(({ data }) => {
      if (!cancelled && data) setLoyaltyProgram(data)
    }).catch(() => {})
    return () => { cancelled = true }
  }, [loyaltyEnabled])

  if (!canUseCoupon && !canUseGiftCard && !canUseLoyaltyVoucher) return null

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return toast.error('Хөнгөлөлтийн кодоо оруулна уу')
    setLoadingCoupon(true)
    setCouponCondition(null)
    try {
      const result = await applyCoupon(couponCode.trim())
      if (result?.success) {
        toast.success('Хөнгөлөлтийн код амжилттай идэвхжлээ')
        setCouponCode('')
        setCouponCondition(null)
      } else if (result?.condition) {
        setCouponCondition(result.condition)
      } else {
        toast.error(result?.error || 'Хөнгөлөлтийн код буруу эсвэл хугацаа дууссан байна')
      }
    } catch (e) {
      toast.error('Алдаа гарлаа')
      console.error(e)
    } finally {
      setLoadingCoupon(false)
    }
  }

  const handleApplyGiftCard = async () => {
    if (!giftCardCode.trim()) return toast.error('Бэлгийн картын кодыг оруулна уу')
    setLoadingGiftCard(true)
    try {
      const success = await applyGiftCard(giftCardCode.trim())
      if (success) {
        toast.success('Бэлгийн карт амжилттай идэвхжлээ')
        setGiftCardCode('')
      } else {
        toast.error('Бэлгийн картын код буруу эсвэл хэрэглэгдсэн байна')
      }
    } catch (e) {
      toast.error('Алдаа гарлаа')
      console.error(e)
    } finally {
      setLoadingGiftCard(false)
    }
  }

  const getCouponInfo = () => {
    if (!appliedCoupon) return null
    const discount = appliedCoupon.discount || appliedCoupon
    let discountText = ''
    let isExceeding = false

    if (discount.type === 'productDiscount' || discount.type === 'order') {
      let valueType = discount.valueType
      let valuePercent = discount.valuePercent
      let valueAmount = discount.valueAmount
      if (!valueType && discount.type === 'productDiscount' && discount.id && allProducts?.length) {
        for (const p of allProducts) {
          const pd = p.product || p
          const match = (p.discounts || pd?.discounts)?.find((d: any) =>
            d.type === 'productDiscount' && d.method === 'code' && d.id === discount.id,
          )
          if (match) {
            valueType = match.valueType
            valuePercent = match.valuePercent
            valueAmount = match.valueAmount
            break
          }
        }
      }
      if (valueType === 'percent' && valuePercent) discountText = `${valuePercent}% хямдрал`
      else if (valueType === 'amount' && valueAmount) {
        discountText = `${formatPrice(valueAmount)} хямдрал`
        isExceeding = valueAmount > subtotal
      } else if (valueType === 'free') discountText = 'Үнэгүй'
    } else if (discount.type === 'buyXgetY') {
      let matchMode = discount.buyXMatchMode
      let triggerCount = discount.triggerTargets?.length || 1
      let minQty = discount.buyXMinQuantity || 1
      let maxReward = discount.getYMaxQuantity || 1
      if (!matchMode && discount.id && allProducts?.length) {
        for (const p of allProducts) {
          if (p.isRewardItem) continue
          const pd = p.product || p
          const match = (p.discounts || pd?.discounts)?.find((d: any) => d.type === 'buyXgetY' && d.id === discount.id)
          if (match) {
            matchMode = match.buyXMatchMode
            triggerCount = match.triggerTargets?.length || 1
            minQty = match.buyXMinQuantity || 1
            maxReward = match.getYMaxQuantity || 1
            break
          }
        }
      }
      const buyCount = matchMode === 'all' ? triggerCount * minQty : minQty
      discountText = `${buyCount} авбал ${maxReward} бэлэг`
    } else if (discount.type === 'freeShipping' || appliedCoupon.freeShipping) {
      discountText = 'Үнэгүй хүргэлт'
    }
    if (!discountText && couponDiscount > 0) discountText = `${formatPrice(couponDiscount)} хямдрал`
    return { discountText: discountText || 'Хөнгөлөлт идэвхжсэн', isExceeding, discountName: discount.name || appliedCoupon.name }
  }

  const couponInfo = getCouponInfo()
  const giftCardAmount = Number(appliedGiftCard?.amount) || 0
  const isGiftCardExceeding = giftCardAmount > subtotal

  const CouponContent = (
    <div className="space-y-3">
      {appliedCoupon && couponInfo ? (
        <>
          <div
            className="flex items-start justify-between gap-3 p-3 rounded-xl"
            style={{ background: 'rgba(96,165,250,0.06)', border: '1px dashed rgba(96,165,250,0.4)' }}
          >
            <div className="flex items-start gap-3 min-w-0 flex-1">
              {(appliedCoupon.freeShipping || (appliedCoupon.discount || appliedCoupon)?.type === 'freeShipping')
                ? <Truck className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#93c5fd' }} />
                : <TicketPercent className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#93c5fd' }} />
              }
              <div className="flex flex-col gap-1.5 min-w-0">
                <div className="text-[13px] font-semibold text-white truncate">{couponInfo.discountName}</div>
                <div className="text-[11.5px]" style={{ color: 'rgba(255,255,255,0.6)' }}>{couponInfo.discountText}</div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span
                    className="text-[10.5px] font-mono font-semibold tabular-nums px-1.5 py-0.5 rounded"
                    style={{ background: 'rgba(96,165,250,0.16)', color: '#93c5fd', border: '1px solid rgba(96,165,250,0.3)' }}
                  >
                    {(appliedCoupon.discount || appliedCoupon)?.code}
                  </span>
                  {couponDiscount > 0 && (
                    <span
                      className="text-[10.5px] font-semibold tabular-nums px-1.5 py-0.5 rounded"
                      style={{ background: 'rgba(244,63,94,0.16)', color: '#fda4af', border: '1px solid rgba(244,63,94,0.3)' }}
                    >
                      −{formatPrice(couponDiscount)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => { removeCoupon(); setCouponCondition(null); toast.success('Хөнгөлөлтийн код хасагдлаа') }}
              className="h-7 w-7 grid place-items-center rounded-full cursor-pointer shrink-0 transition-colors"
              style={{ color: 'rgba(255,255,255,0.5)' }}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {beatenByMessage(appliedCoupon.beatenBy) && (
            <p className="text-[11.5px] leading-snug" style={{ color: 'rgba(255,255,255,0.6)' }}>{beatenByMessage(appliedCoupon.beatenBy)}</p>
          )}

          {couponInfo.isExceeding && (
            <div
              className="flex items-start gap-2 p-3 rounded-xl"
              style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)' }}
            >
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: '#fcd34d' }} />
              <div className="text-[11.5px] leading-snug" style={{ color: 'rgba(252,211,77,0.85)' }}>
                <div className="font-medium">Анхаар!</div>
                <div className="mt-1">
                  Хөнгөлөлтийн дүн нь сагсны дүн ({formatPrice(subtotal)})-ээс их байна.
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="relative">
            <TicketPercent className="absolute left-3.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: 'rgba(255,255,255,0.4)' }} />
            <input
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
              placeholder="Хөнгөлөлтийн код"
              className="w-full h-11 pl-10 pr-24 rounded-xl text-[13px] outline-none focus:ring-0 placeholder:text-white/30"
              style={inputStyle}
            />
            <button
              type="button"
              onClick={handleApplyCoupon}
              disabled={loadingCoupon}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[11.5px] font-semibold cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{ background: 'white', color: '#0a0a0c' }}
            >
              {loadingCoupon ? <LoaderCircle className="h-3 w-3 animate-spin" /> : <CircleCheck className="h-3 w-3" />}
              Идэвхжүүлэх
            </button>
          </div>

          {couponCondition && (
            <div
              className="relative overflow-hidden rounded-xl"
              style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)' }}
            >
              <div className="p-3.5">
                <div className="flex items-start gap-3">
                  <div
                    className="flex items-center justify-center w-9 h-9 rounded-lg shrink-0"
                    style={{ background: 'rgba(245,158,11,0.16)', border: '1px solid rgba(245,158,11,0.3)' }}
                  >
                    {couponCondition.discountType === 'freeShipping'
                      ? <Truck className="w-4 h-4" style={{ color: '#fcd34d' }} />
                      : couponCondition.discountType === 'buyXgetY'
                        ? <Gift className="w-4 h-4" style={{ color: '#fcd34d' }} />
                        : <TicketPercent className="w-4 h-4" style={{ color: '#fcd34d' }} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold" style={{ color: '#fcd34d' }}>{couponCondition.discountName}</div>
                    <div className="text-[11.5px] mt-0.5" style={{ color: 'rgba(252,211,77,0.8)' }}>
                      {couponCondition.type === 'minOrderAmount'
                        ? `${formatPrice(couponCondition.required)} захиалгад ${formatPrice(couponCondition.required - couponCondition.current)} дутуу байна`
                        : `${couponCondition.required} ширхэгээс ${couponCondition.required - couponCondition.current} дутуу байна`}
                    </div>
                    {couponCondition.triggerProducts?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {couponCondition.triggerProducts.map((p: any) => (
                          <Link
                            key={p.id}
                            href={navigateUrl(`/product/${p.customId}`, pathname)}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors"
                            style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(245,158,11,0.25)' }}
                          >
                            {p.image && <img src={formatImageURL(p.image)} alt={p.name} className="w-7 h-7 rounded object-cover" />}
                            <span className="text-[11px] font-medium text-white max-w-[120px] truncate">{p.name}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCouponCondition(null)}
                    className="h-7 w-7 grid place-items-center rounded-full cursor-pointer shrink-0 transition-colors"
                    style={{ color: '#fcd34d' }}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="mt-3">
                  <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(245,158,11,0.16)' }}>
                    <div
                      className="h-1.5 rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.min(100, (couponCondition.current / couponCondition.required) * 100)}%`,
                        background: '#fcd34d',
                      }}
                    />
                  </div>
                  <div className="flex justify-between mt-1.5 text-[10.5px]" style={{ color: 'rgba(252,211,77,0.8)' }}>
                    <span className="tabular-nums">{couponCondition.type === 'minOrderAmount' ? formatPrice(couponCondition.current) : `${couponCondition.current} ширхэг`}</span>
                    <span className="tabular-nums">{couponCondition.type === 'minOrderAmount' ? formatPrice(couponCondition.required) : `${couponCondition.required} ширхэг`}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )

  const GiftCardContent = (
    <div className="space-y-3">
      {appliedGiftCard ? (
        <>
          <div
            className="flex items-center justify-between gap-3 p-3 rounded-xl"
            style={{ background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.3)' }}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div
                className="flex items-center justify-center w-9 h-9 rounded-lg shrink-0"
                style={{ background: 'rgba(168,85,247,0.16)', border: '1px solid rgba(168,85,247,0.3)' }}
              >
                <Gift className="w-4 h-4" style={{ color: '#d8b4fe' }} />
              </div>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold" style={{ color: '#d8b4fe' }}>Карт #{appliedGiftCard.customId}</div>
                <div className="text-[11.5px] mt-0.5 tabular-nums" style={{ color: 'rgba(216,180,254,0.8)' }}>
                  {formatPrice(giftCardAmount)} хөнгөлөлт
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => { removeGiftCard(); toast.success('Бэлгийн карт хасагдлаа') }}
              className="h-7 w-7 grid place-items-center rounded-full cursor-pointer shrink-0 transition-colors"
              style={{ color: '#d8b4fe' }}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {isGiftCardExceeding && (
            <div
              className="flex items-start gap-2 p-3 rounded-xl"
              style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)' }}
            >
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: '#fcd34d' }} />
              <div className="text-[11.5px] leading-snug" style={{ color: 'rgba(252,211,77,0.85)' }}>
                <div className="font-medium">Анхаар!</div>
                <div className="mt-1">
                  Бэлгийн картын дүн ({formatPrice(giftCardAmount)}) нь сагсны дүн ({formatPrice(subtotal)})-ээс их байна.
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="relative">
          <Gift className="absolute left-3.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: 'rgba(255,255,255,0.4)' }} />
          <input
            value={giftCardCode}
            onChange={(e) => setGiftCardCode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleApplyGiftCard()}
            placeholder="Бэлгийн картын код"
            className="w-full h-11 pl-10 pr-24 rounded-xl text-[13px] outline-none focus:ring-0 placeholder:text-white/30"
            style={inputStyle}
          />
          <button
            type="button"
            onClick={handleApplyGiftCard}
            disabled={loadingGiftCard}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[11.5px] font-semibold cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ background: 'white', color: '#0a0a0c' }}
          >
            {loadingGiftCard ? <LoaderCircle className="h-3 w-3 animate-spin" /> : <CircleCheck className="h-3 w-3" />}
            Идэвхжүүлэх
          </button>
        </div>
      )}
    </div>
  )

  const LoyaltyContent = (
    <div className="space-y-3">
      {appliedLoyaltyVoucher ? (
        <>
          <div
            className="flex items-center justify-between gap-3 p-3 rounded-xl"
            style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.3)' }}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div
                className="flex items-center justify-center w-9 h-9 rounded-lg shrink-0"
                style={{ background: 'rgba(52,211,153,0.16)', border: '1px solid rgba(52,211,153,0.3)' }}
              >
                <Ticket className="w-4 h-4" style={{ color: '#6ee7b7' }} />
              </div>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold font-mono truncate" style={{ color: '#6ee7b7' }}>{appliedLoyaltyVoucher.code}</div>
                <div className="text-[11.5px] mt-0.5 inline-flex items-center gap-1" style={{ color: 'rgba(110,231,183,0.8)' }}><CircleCheck className="w-3 h-3" />Идэвхжсэн</div>
              </div>
            </div>
            <div className="text-[13px] font-bold tabular-nums shrink-0" style={{ color: '#6ee7b7' }}>−{formatPrice(appliedVoucherAmount)}</div>
            <button
              type="button"
              onClick={() => { removeLoyaltyVoucher(); toast.success('Ваучер хасагдлаа') }}
              className="h-7 w-7 grid place-items-center rounded-full cursor-pointer shrink-0 transition-colors"
              style={{ color: '#6ee7b7' }}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {!loyaltyProgram?.voucherPartialUse && voucherExceedsCart ? (
            <div
              className="flex items-start gap-2 p-3 rounded-xl"
              style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)' }}
            >
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: '#fcd34d' }} />
              <div className="text-[11.5px] leading-snug" style={{ color: 'rgba(252,211,77,0.85)' }}>
                <div className="font-medium">Анхаар!</div>
                <div className="mt-1">
                  Ваучер захиалгад бүтнээрээ зарцуулагдана. Захиалгын дүн ваучерын дүнгээс бага тул үлдэх <span className="font-semibold tabular-nums">{formatPrice(appliedVoucherAmount - subtotal)}</span> буцаагдахгүй.
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-1.5 px-1 text-[11.5px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{loyaltyProgram?.voucherPartialUse ? 'Ашиглаагүй үлдэгдэл хадгалагдаж, дараагийн захиалгад ашиглагдана.' : 'Ваучер захиалгад бүтнээрээ зарцуулагдана.'}</span>
            </div>
          )}
        </>
      ) : loyaltyVouchers.length === 0 ? (
        <div className="text-[11.5px] px-1" style={{ color: 'rgba(255,255,255,0.55)' }}>
          Танд идэвхтэй ваучер алга байна. <Link href={navigateUrl('/loyalty', pathname)} className="underline hover:text-white">Оноогоо ваучер болгох</Link>
        </div>
      ) : (
        <div className="space-y-2">
          {loyaltyVouchers.map((voucher: any) => (
            <div
              key={voucher.id}
              className="flex items-center gap-3 p-3 rounded-xl"
              style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.13)' }}
            >
              <div
                className="flex items-center justify-center w-9 h-9 rounded-lg shrink-0"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.13)' }}
              >
                <Ticket className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.7)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold font-mono text-white truncate">{voucher.code}</div>
                <div className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{voucher.expireAt ? `${formatDateTimeNormal(voucher.expireAt)} хүртэл` : 'Хугацаагүй'}</div>
              </div>
              <div className="text-[13px] font-semibold tabular-nums text-white shrink-0">{formatPrice(Number(voucher.remainingAmount ?? voucher.amount) || 0)}</div>
              <button
                type="button"
                onClick={() => { applyLoyaltyVoucher(voucher); toast.success('Ваучер идэвхжлээ') }}
                className="h-8 px-3 rounded-lg text-[11.5px] font-semibold cursor-pointer transition-opacity hover:opacity-90 shrink-0"
                style={{ background: 'white', color: '#0a0a0c' }}
              >
                Ашиглах
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  const tabDefs = [
    ...(canUseCoupon ? [{ id: 'coupon' as const, label: 'Код', Icon: TicketPercent, content: CouponContent }] : []),
    ...(canUseGiftCard ? [{ id: 'giftcard' as const, label: <><span className="sm:hidden">Карт</span><span className="hidden sm:inline">Бэлгийн карт</span></>, Icon: Gift, content: GiftCardContent }] : []),
    ...(canUseLoyaltyVoucher ? [{ id: 'loyalty' as const, label: 'Ваучер', Icon: Ticket, content: LoyaltyContent }] : []),
  ]

  if (tabDefs.length === 1) return tabDefs[0].content

  const visibleTab = tabDefs.some(t => t.id === activeTab) ? activeTab : tabDefs[0].id

  return (
    <div>
      <div
        className="flex items-center gap-1 rounded-xl p-1 mb-3 w-full"
        style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        {tabDefs.map(t => {
          const active = visibleTab === t.id
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className="flex-1 inline-flex h-8 items-center justify-center gap-1.5 px-2 rounded-lg text-[11.5px] font-medium transition-all cursor-pointer"
              style={active
                ? { background: 'rgba(255,255,255,0.08)', color: 'white' }
                : { background: 'transparent', color: 'rgba(255,255,255,0.55)' }}
            >
              <t.Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          )
        })}
      </div>
      {tabDefs.find(t => t.id === visibleTab)?.content}
    </div>
  )
}
